import React, { useEffect, useState, useRef } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Inbox as InboxIcon, Send, Loader2, ChevronLeft, CheckCircle2, Clock, AlertCircle,
  UserCog, Sparkles, RefreshCw, Trash2, CheckCheck,
} from "lucide-react";
import { InboxAttachmentPicker, InboxAttachmentImage } from "@/components/InboxAttachment";

function StatusBadge({ status }) {
  const map = {
    open: { bg: "bg-[#FBBF24]/12 border-[#FBBF24]/40 text-[#FBBF24]", label: "Open", Icon: AlertCircle },
    in_progress: { bg: "bg-[#1E3A8A]/25 border-[#3B82F6]/40 text-[#93C5FD]", label: "In Progress", Icon: Clock },
    resolved: { bg: "bg-emerald-500/12 border-emerald-400/40 text-emerald-300", label: "Resolved", Icon: CheckCircle2 },
  };
  const cfg = map[status] || map.open;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded border ${cfg.bg}`}>
      <cfg.Icon size={11} /> {cfg.label}
    </span>
  );
}

function AdminThreadView({ threadId, isSuper, assignees, onBack, onChanged }) {
  const [thread, setThread] = useState(null);
  const [busy, setBusy] = useState(true);
  const [reply, setReply] = useState("");
  const [attachment, setAttachment] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/inbox/${threadId}`);
      setThread(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [threadId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim() && !attachment) return;
    setSending(true);
    try {
      await api.post(`/inbox/${threadId}/reply`, { body: reply.trim(), attachment });
      setReply(""); setAttachment("");
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSending(false); }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/admin/inbox/${threadId}/status`, { status });
      toast.success(`Status set to ${status}`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const assign = async (id) => {
    try {
      await api.patch(`/admin/inbox/${threadId}/assign`, { assignee_id: id || null });
      toast.success(id ? "Assigned." : "Unassigned.");
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (busy || !thread) return <div className="text-white/50 flex items-center gap-2 p-6"><Loader2 size={14} className="animate-spin" /> Loading thread…</div>;

  return (
    <div className="glass-card p-5 space-y-4" data-testid={`admin-inbox-thread-${threadId}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <button onClick={onBack} className="text-xs text-white/50 hover:text-white inline-flex items-center gap-1" data-testid="admin-inbox-back">
            <ChevronLeft size={12} /> All threads
          </button>
          <div className="mt-1 font-display text-xl font-black truncate">{thread.subject}</div>
          <div className="text-[11px] text-white/50 mt-0.5">
            From <strong className="text-white/80">{thread.user_name}</strong> · <span className="font-mono-num">{thread.user_email}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={thread.status} />
          <select
            value={thread.status}
            onChange={(e) => setStatus(e.target.value)}
            className="text-xs bg-black/40 border border-white/10 rounded px-2 py-1"
            data-testid="admin-inbox-status-select"
          >
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
          {isSuper && (
            <select
              value={thread.assigned_to || ""}
              onChange={(e) => assign(e.target.value)}
              className="text-xs bg-black/40 border border-white/10 rounded px-2 py-1"
              data-testid="admin-inbox-assign-select"
            >
              <option value="">Unassigned</option>
              {assignees.map((a) => (
                <option key={a.id} value={a.id}>{a.full_name || a.email} {a.role === "admin" ? "· Super" : ""}</option>
              ))}
            </select>
          )}
        </div>
      </div>
      <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="admin-inbox-thread-messages">
        {thread.messages.map((m) => {
          const fromAdmin = m.from_role === "admin" || m.from_role === "sub_admin";
          const isAI = m.from_role === "ai_assistant";
          const bubbleClass = isAI
            ? "bg-gradient-to-br from-[#7C3AED]/20 to-[#3B82F6]/15 border-[#A78BFA]/50"
            : fromAdmin
              ? "bg-[#FBBF24]/10 border-[#FBBF24]/30"
              : "bg-[#1E3A8A]/40 border-[#1E3A8A]/60";
          const labelClass = isAI ? "text-[#C4B5FD]" : fromAdmin ? "text-[#FBBF24]" : "text-[#93C5FD]";
          const label = isAI
            ? (<span className="inline-flex items-center gap-1"><Sparkles size={10} /> Zen · AI Auto-reply</span>)
            : `${m.from_name || m.from_role} · ${m.from_role === "admin" ? "SUPER" : m.from_role.toUpperCase()}`;
          return (
            <div key={m.id} className={`flex ${fromAdmin ? "justify-end" : "justify-start"}`}
              data-testid={isAI ? "admin-inbox-message-ai" : undefined}>
              <div className={`max-w-[75%] rounded-lg px-3 py-2 border ${bubbleClass}`}>
                <div className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${labelClass}`}>
                  {label}
                </div>
                {m.body && <div className="text-sm text-white whitespace-pre-wrap break-words">{m.body}</div>}
                {m.has_attachment && m.attachment_url && (
                  <InboxAttachmentImage url={m.attachment_url} />
                )}
                <div className="text-[10px] text-white/40 mt-1 font-mono-num">
                  {isAI && <span className="mr-1 text-[#A78BFA]/70">auto ·</span>}
                  {m.created_at?.slice(0, 16).replace("T", " ")}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="border-t border-white/10 pt-3 space-y-2">
        <div className="flex items-end gap-2">
          <textarea className="zfd-input flex-1 min-h-[64px]" value={reply} onChange={(e) => setReply(e.target.value)}
            maxLength={4000} placeholder="Reply to the operator…" data-testid="admin-inbox-reply-input" />
          <button type="submit" disabled={sending || (!reply.trim() && !attachment)} className="btn-deploy px-4 py-2.5 inline-flex items-center gap-2 disabled:opacity-40" data-testid="admin-inbox-reply-submit">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
          </button>
        </div>
        <InboxAttachmentPicker
          value={attachment}
          onChange={setAttachment}
          onError={(m) => toast.error(m)}
          disabled={sending}
        />
      </form>
    </div>
  );
}

export default function AdminInbox() {
  const { user } = useAuth();
  const isSuper = user?.role === "admin";
  const [threads, setThreads] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState({ status: "", assigned_to: "" });
  const [checked, setChecked] = useState(new Set()); // thread IDs selected for bulk actions
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.assigned_to) params.assigned_to = filter.assigned_to;
      const [t, a] = await Promise.all([
        api.get("/admin/inbox", { params }),
        api.get("/admin/inbox/assignable"),
      ]);
      setThreads(t.data);
      setAssignees(a.data);
      // Prune stale check IDs that no longer exist in the current list.
      setChecked((prev) => {
        const alive = new Set(t.data.map((x) => x.id));
        const next = new Set();
        for (const id of prev) if (alive.has(id)) next.add(id);
        return next;
      });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter.status, filter.assigned_to]);

  const toggleCheck = (id, e) => {
    e?.stopPropagation();
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const allVisibleSelected = threads.length > 0 && threads.every((t) => checked.has(t.id));
  const toggleSelectAll = () => {
    setChecked((prev) => {
      if (allVisibleSelected) return new Set();
      const next = new Set(prev);
      threads.forEach((t) => next.add(t.id));
      return next;
    });
  };

  const runBulk = async (action) => {
    // Two modes: explicit IDs when the admin ticked rows, otherwise "all
    // matching the current filter" — with a spelled-out confirm.
    const usingSelection = checked.size > 0;
    const scopeCount = usingSelection ? checked.size : threads.length;
    if (scopeCount === 0) return toast.error("No threads to act on with this filter.");
    const verb = action === "resolve" ? "mark as resolved" : "DELETE (irreversible)";
    const scope = usingSelection ? `the ${scopeCount} selected` : `ALL ${scopeCount} threads matching the current filter`;
    if (!window.confirm(`Are you sure you want to ${verb} ${scope}?${action === "delete" ? "\nMessages will be permanently removed." : ""}`)) return;
    const body = usingSelection
      ? { thread_ids: Array.from(checked) }
      : { filter_status: filter.status || "all" };
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/admin/inbox/bulk-${action}`, body);
      if (action === "resolve") toast.success(`Marked ${data.resolved} as resolved.`);
      else toast.success(`Deleted ${data.threads_deleted} threads (${data.messages_deleted} messages).`);
      setChecked(new Set());
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBulkBusy(false); }
  };

  if (loading) return (
    <div className="text-white/50 flex items-center gap-2 p-6"><Loader2 size={16} className="animate-spin" /> Loading inbox…</div>
  );

  if (selected) {
    return (
      <AdminThreadView threadId={selected} isSuper={isSuper} assignees={assignees}
        onBack={() => { setSelected(null); load(); }} onChanged={load} />
    );
  }

  const unread = threads.filter((t) => t.unread_by_admin).length;

  return (
    <div className="space-y-4" data-testid="admin-inbox">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">
            <Sparkles size={12} /> Support Inbox
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight mt-1">
            {threads.length} thread{threads.length === 1 ? "" : "s"} · <span className="text-[#DC2626]">{unread} unread</span>
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="text-xs bg-black/40 border border-white/10 rounded px-2 py-1.5" data-testid="admin-inbox-filter-status">
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
          {isSuper && (
            <select value={filter.assigned_to} onChange={(e) => setFilter({ ...filter, assigned_to: e.target.value })}
              className="text-xs bg-black/40 border border-white/10 rounded px-2 py-1.5" data-testid="admin-inbox-filter-assignee">
              <option value="">All assignees</option>
              <option value="unassigned">Unassigned</option>
              {assignees.map((a) => (
                <option key={a.id} value={a.id}>{a.full_name || a.email}</option>
              ))}
            </select>
          )}
          <button onClick={load} className="text-xs px-3 py-1.5 border border-white/15 rounded hover:border-[#FBBF24] hover:text-[#FBBF24] inline-flex items-center gap-1" data-testid="admin-inbox-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Bulk actions toolbar — visible whenever any threads are listed. */}
      {threads.length > 0 && (
        <div className="glass-card p-3 flex items-center gap-2 flex-wrap" data-testid="admin-inbox-bulk-toolbar">
          <label className="flex items-center gap-2 text-xs text-white/75 cursor-pointer select-none">
            <input
              type="checkbox"
              className="accent-[#FBBF24]"
              checked={allVisibleSelected}
              ref={(el) => { if (el) el.indeterminate = checked.size > 0 && !allVisibleSelected; }}
              onChange={toggleSelectAll}
              data-testid="admin-inbox-select-all"
            />
            <span className="font-mono-num">{checked.size}</span> selected
            {checked.size === 0 && <span className="text-white/40">— actions apply to <strong className="text-white/70">all {threads.length} threads in this view</strong></span>}
          </label>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => runBulk("resolve")}
              disabled={bulkBusy}
              className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 font-bold uppercase tracking-widest disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="admin-inbox-bulk-resolve"
            >
              <CheckCheck size={13} /> {checked.size > 0 ? `Resolve ${checked.size}` : "Resolve all"}
            </button>
            <button
              type="button"
              onClick={() => runBulk("delete")}
              disabled={bulkBusy}
              className="text-xs px-3 py-1.5 rounded bg-[#DC2626] hover:bg-red-700 font-bold uppercase tracking-widest disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="admin-inbox-bulk-delete"
            >
              <Trash2 size={13} /> {checked.size > 0 ? `Delete ${checked.size}` : "Delete all"}
            </button>
          </div>
        </div>
      )}

      {threads.length === 0 ? (
        <div className="glass-card p-10 text-center text-white/55" data-testid="admin-inbox-empty">
          <InboxIcon size={26} className="text-white/25 mx-auto mb-2" />
          No threads match this filter. When operators send messages, they land here.
        </div>
      ) : (
        <div className="glass-card p-4" data-testid="admin-inbox-list">
          <div className="divide-y divide-white/5">
            {threads.map((t) => (
              <div
                key={t.id}
                className="w-full py-3 px-2 hover:bg-white/[0.03] rounded flex items-start gap-3"
                data-testid={`admin-inbox-thread-item-${t.id}`}
              >
                <input
                  type="checkbox"
                  className="mt-2 accent-[#FBBF24]"
                  checked={checked.has(t.id)}
                  onChange={(e) => toggleCheck(t.id, e)}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`admin-inbox-check-${t.id}`}
                  aria-label={`Select thread ${t.subject}`}
                />
                <button
                  type="button"
                  onClick={() => setSelected(t.id)}
                  className="min-w-0 flex-1 flex items-start gap-3 text-left"
                  data-testid={`admin-inbox-thread-open-${t.id}`}
                >
                  <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${t.unread_by_admin ? "bg-[#DC2626]" : "bg-white/15"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className={`text-sm truncate ${t.unread_by_admin ? "font-bold text-white" : "text-white/85"}`}>{t.subject}</div>
                      <StatusBadge status={t.status} />
                      {t.assigned_name && (
                        <span className="text-[10px] uppercase tracking-widest text-[#FBBF24] bg-[#FBBF24]/10 border border-[#FBBF24]/30 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                          <UserCog size={10} /> {t.assigned_name}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-white/55 truncate mt-0.5">
                      <strong className="text-white/80">{t.user_name}</strong> · {t.user_email} — {t.last_message_preview}
                    </div>
                    <div className="text-[10px] text-white/35 mt-0.5 font-mono-num">Updated {t.updated_at?.slice(0, 16).replace("T", " ")} · {t.message_count} messages</div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
